import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdminEmail } from '../constants/admin';
import { adminApi } from '../utils/adminApi';
import { formatReturnsCentered, formatExpectedReturn } from '../utils/returns';
import NotFound from './NotFound';
import { BackendAccess, SiteContentEditor } from './AdminContent';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'custom', label: 'Custom playbooks' },
  { id: 'odikai', label: 'Odikai playbooks' },
  { id: 'content', label: 'Site content' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};


// Rename form shown in place of a playbook's name
function RenameForm({ value, onSave, onDone, busy }) {
  const [draft, setDraft] = useState(value);
  return (
    <form
      className="admin-inline-form"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!draft.trim() || draft.trim() === value) { onDone(); return; }
        if (await onSave(draft.trim())) onDone();
      }}
    >
      <input
        className="input admin-input-sm"
        value={draft}
        maxLength={120}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onDone(); }}
        aria-label="Playbook name"
        autoFocus
      />
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>Save</button>
      <button type="button" className="btn btn-sm" onClick={onDone}>Cancel</button>
    </form>
  );
}

function Overview({ users, playbooks, onGo }) {
  const custom = playbooks.filter((p) => !p.is_prebuilt);
  const odikai = playbooks.filter((p) => p.is_prebuilt);
  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
  const cards = [
    { label: 'Accounts', value: users.length, tab: 'accounts' },
    { label: 'New this week', value: users.filter((u) => u.created_at && new Date(u.created_at).getTime() > weekAgo).length, tab: 'accounts' },
    { label: 'Disabled accounts', value: users.filter((u) => u.disabled).length, tab: 'accounts' },
    { label: 'Custom playbooks', value: custom.length, tab: 'custom' },
    { label: 'Public custom playbooks', value: custom.filter((p) => p.is_public).length, tab: 'custom' },
    { label: 'Featured playbooks', value: custom.filter((p) => p.creator_type === 'special').length, tab: 'custom' },
    { label: 'Odikai playbooks shown', value: `${odikai.filter((p) => p.is_public).length}/${odikai.length}`, tab: 'odikai' },
  ];
  return (
    <>
      <div className="admin-overview">
        {cards.map((c) => (
          <button type="button" key={c.label} className="admin-metric" onClick={() => onGo(c.tab)}>
            <span className="admin-metric-value">{c.value}</span>
            <span className="admin-metric-label">{c.label}</span>
          </button>
        ))}
      </div>
      <BackendAccess />
    </>
  );
}

function Accounts({ users, run, busyId, notify }) {
  const [search, setSearch] = useState('');
  const [limits, setLimits] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.email || ''} ${u.display_name || ''} ${u.uid}`.toLowerCase().includes(q));
  }, [users, search]);

  return (
    <>
      <div className="admin-toolbar">
        <input
          className="input admin-search"
          placeholder="Search by email, name or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search accounts"
        />
        <span className="admin-count">{shown.length} of {users.length}</span>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Joined</th>
              <th scope="col">Last sign-in</th>
              <th scope="col">Playbooks</th>
              <th scope="col">Limit</th>
              <th scope="col">Status</th>
              <th scope="col"><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => {
              const busy = busyId === u.uid;
              const limitDraft = limits[u.uid] ?? String(u.available_playbooks);
              return (
                <React.Fragment key={u.uid}>
                  <tr className={u.disabled ? 'is-disabled' : ''}>
                    <td data-label="Account">
                      <div className="admin-account">
                        <span className="admin-primary">{u.email || '(no email)'}</span>
                        <span className="admin-secondary">
                          {u.display_name || 'No name'}
                          {!u.email_verified && ' · unverified'}
                        </span>
                      </div>
                    </td>
                    <td data-label="Joined">{formatDate(u.created_at)}</td>
                    <td data-label="Last sign-in">{formatDate(u.last_sign_in)}</td>
                    <td data-label="Playbooks">
                      {u.playbooks}{u.public_playbooks ? <span className="admin-secondary"> ({u.public_playbooks} public)</span> : null}
                    </td>
                    <td data-label="Limit">
                      {u.is_super_admin ? u.available_playbooks : (
                        <form
                          className="admin-inline-form"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            const n = Number(limitDraft);
                            if (!Number.isInteger(n) || n < 0 || n > 1000) { notify('Limit must be a whole number from 0 to 1000.', true); return; }
                            const ok = await run(u.uid, () => adminApi.updateUser(u.uid, { available_playbooks: n }), `Limit for ${u.email} set to ${n}.`);
                            if (ok) setLimits((l) => { const next = { ...l }; delete next[u.uid]; return next; });
                          }}
                        >
                          <input
                            className="input admin-input-num"
                            type="number"
                            min="0"
                            max="1000"
                            value={limitDraft}
                            onChange={(e) => setLimits((l) => ({ ...l, [u.uid]: e.target.value }))}
                            aria-label={`Playbook limit for ${u.email}`}
                          />
                          {limitDraft !== String(u.available_playbooks) && (
                            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>Save</button>
                          )}
                        </form>
                      )}
                    </td>
                    <td data-label="Status">
                      {u.is_super_admin
                        ? <span className="tag tag-purple">Super admin</span>
                        : u.disabled ? <span className="tag admin-tag-off">Disabled</span> : <span className="tag">Active</span>}
                    </td>
                    <td className="admin-actions">
                      {!u.is_super_admin && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={busy}
                            onClick={() => run(u.uid, () => adminApi.updateUser(u.uid, { disabled: !u.disabled }),
                              u.disabled ? `${u.email} can sign in again.` : `${u.email} is disabled and signed out.`)}
                          >
                            {u.disabled ? 'Enable' : 'Disable'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={busy}
                            title="Ends every signed-in session for this account"
                            onClick={() => run(u.uid, () => adminApi.revokeSessions(u.uid), `${u.email} is signed out everywhere.`)}
                          >
                            Sign out
                          </button>
                          {u.email && u.providers.includes('password') && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              disabled={busy}
                              onClick={() => run(u.uid, () => sendPasswordResetEmail(auth, u.email), `Password reset email sent to ${u.email}.`, false)}
                            >
                              Reset password
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={busy}
                            onClick={() => { setDeleting(deleting === u.uid ? null : u.uid); setConfirmText(''); }}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {deleting === u.uid && (
                    <tr className="admin-confirm-row">
                      <td colSpan={7}>
                        <form
                          className="admin-confirm"
                          onSubmit={async (e) => {
                            e.preventDefault();
                            const ok = await run(u.uid, () => adminApi.deleteUser(u.uid), `${u.email} and their playbooks were deleted.`);
                            if (ok) setDeleting(null);
                          }}
                        >
                          <p>
                            This permanently deletes the account, its profile and its {u.playbooks} playbook{u.playbooks === 1 ? '' : 's'}. It cannot be undone.
                            Type <strong>{u.email || u.uid}</strong> to confirm.
                          </p>
                          <div className="admin-inline-form">
                            <input
                              className="input admin-input-sm"
                              value={confirmText}
                              onChange={(e) => setConfirmText(e.target.value)}
                              aria-label="Type the email to confirm"
                              autoFocus
                            />
                            <button type="submit" className="btn btn-sm btn-danger" disabled={busy || confirmText.trim() !== (u.email || u.uid)}>
                              Delete permanently
                            </button>
                            <button type="button" className="btn btn-sm" onClick={() => setDeleting(null)}>Cancel</button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={7} className="admin-empty">No accounts match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CustomPlaybooks({ playbooks, run, busyId }) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [renaming, setRenaming] = useState(null);

  const custom = useMemo(() => playbooks.filter((p) => !p.is_prebuilt), [playbooks]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return custom.filter((p) => {
      if (filter === 'public' && !p.is_public) return false;
      if (filter === 'private' && p.is_public) return false;
      if (filter === 'featured' && p.creator_type !== 'special') return false;
      if (!q) return true;
      return `${p.name} ${p.owner_email || ''} ${p.owner_name || ''} ${p.id}`.toLowerCase().includes(q);
    });
  }, [custom, search, filter]);

  return (
    <>
      <div className="admin-toolbar">
        <input
          className="input admin-search"
          placeholder="Search by name or owner"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search custom playbooks"
        />
        <select className="selector-input admin-filter" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter custom playbooks">
          <option value="all">All</option>
          <option value="public">Public</option>
          <option value="private">Private</option>
          <option value="featured">Featured</option>
        </select>
        <span className="admin-count">{shown.length} of {custom.length}</span>
      </div>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Playbook</th>
              <th scope="col">Owner</th>
              <th scope="col">Created</th>
              <th scope="col">Strategies</th>
              <th scope="col">Backtest return</th>
              <th scope="col">Visibility</th>
              <th scope="col"><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => {
              const busy = busyId === p.id;
              const featured = p.creator_type === 'special';
              return (
                <tr key={p.id}>
                  <td data-label="Playbook">
                    {renaming === p.id ? (
                      <RenameForm
                        value={p.name}
                        busy={busy}
                        onDone={() => setRenaming(null)}
                        onSave={(name) => run(p.id, () => adminApi.updatePlaybook(p.id, { name }), `Renamed to ${name}.`)}
                      />
                    ) : (
                      <div className="admin-account">
                        <span className="admin-primary">
                          {p.is_public ? <Link to={`/playbooks/${p.id}`}>{p.name}</Link> : p.name}
                          {featured && <span className="tag tag-purple admin-tag-inline">Featured</span>}
                        </span>
                        <span className="admin-secondary">{p.playbook_type || 'custom'}{p.event_name ? ` · ${p.event_name}` : ''}</span>
                      </div>
                    )}
                  </td>
                  <td data-label="Owner">
                    <div className="admin-account">
                      <span>{p.owner_email || 'Deleted account'}</span>
                      {p.owner_name && <span className="admin-secondary">{p.owner_name}</span>}
                    </div>
                  </td>
                  <td data-label="Created">{formatDate(p.created_at)}</td>
                  <td data-label="Strategies">{p.strategies_count ?? '—'}</td>
                  <td data-label="Backtest return">{formatExpectedReturn(p.portfolio_returns)}</td>
                  <td data-label="Visibility">
                    <button
                      type="button"
                      className={`admin-switch${p.is_public ? ' on' : ''}`}
                      role="switch"
                      aria-checked={p.is_public}
                      disabled={busy}
                      onClick={() => run(p.id, () => adminApi.updatePlaybook(p.id, { is_public: !p.is_public }),
                        `${p.name} is now ${p.is_public ? 'private' : 'public'}.`)}
                    >
                      <span className="admin-switch-knob" aria-hidden="true" />
                      {p.is_public ? 'Public' : 'Private'}
                    </button>
                    {p.admin_locked && <span className="admin-secondary admin-locked-note">Locked</span>}
                  </td>
                  <td className="admin-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={busy}
                      title="Featured playbooks are badged on Top playbooks"
                      onClick={() => run(p.id, () => adminApi.updatePlaybook(p.id, { creator_type: featured ? 'user' : 'special' }),
                        featured ? `${p.name} is no longer featured.` : `${p.name} is featured.`)}
                    >
                      {featured ? 'Unfeature' : 'Feature'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={busy}
                      title="A locked playbook's owner cannot change its visibility"
                      onClick={() => run(p.id, () => adminApi.updatePlaybook(p.id, { admin_locked: !p.admin_locked }),
                        p.admin_locked ? `${p.name}'s owner can change its visibility again.` : `${p.name} stays ${p.is_public ? 'public' : 'private'} until you unlock it.`)}
                    >
                      {p.admin_locked ? 'Unlock' : 'Lock'}
                    </button>
                    <button type="button" className="admin-link-button" disabled={busy} onClick={() => setRenaming(p.id)}>
                      Rename
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm btn-danger${confirmDelete === p.id ? ' admin-confirming' : ''}`}
                      disabled={busy}
                      onClick={async () => {
                        if (confirmDelete !== p.id) { setConfirmDelete(p.id); return; }
                        await run(p.id, () => adminApi.deletePlaybook(p.id), `${p.name} was deleted.`);
                        setConfirmDelete(null);
                      }}
                      onBlur={() => setConfirmDelete((c) => (c === p.id ? null : c))}
                    >
                      {confirmDelete === p.id ? 'Confirm delete' : 'Delete'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {shown.length === 0 && (
              <tr><td colSpan={7} className="admin-empty">No custom playbooks match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function OdikaiPlaybooks({ playbooks, run, busyId }) {
  const [renaming, setRenaming] = useState(null);
  const odikai = useMemo(
    () => playbooks.filter((p) => p.is_prebuilt).sort((a, b) => (a.type || a.id).localeCompare(b.type || b.id)),
    [playbooks]
  );
  return (
    <>
      <p className="admin-note">
        Hidden playbooks disappear from Picks, Compare playbooks and Top playbooks. The daily job keeps updating them, so showing one again brings it back up to date.
      </p>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Playbook</th>
              <th scope="col">Picks</th>
              <th scope="col">Events scored</th>
              <th scope="col">Avg return</th>
              <th scope="col">Last updated</th>
              <th scope="col">On the site</th>
              <th scope="col"><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {odikai.map((p) => {
              const busy = busyId === p.id;
              return (
                <tr key={p.id}>
                  <td data-label="Playbook">
                    {renaming === p.id ? (
                      <RenameForm
                        value={p.name}
                        busy={busy}
                        onDone={() => setRenaming(null)}
                        onSave={(name) => run(p.id, () => adminApi.updatePlaybook(p.id, { name }), `Renamed to ${name}.`)}
                      />
                    ) : (
                      <div className="admin-account">
                        <span className="admin-primary">
                          {p.type ? <Link to={`/picks?playbook=${p.type}`}>{p.name}</Link> : p.name}
                        </span>
                        <span className="admin-secondary">{p.id}</span>
                      </div>
                    )}
                  </td>
                  <td data-label="Picks">{p.picks_count}</td>
                  <td data-label="Events scored">{p.events_scored}</td>
                  <td data-label="Avg return">{formatReturnsCentered(p.avg_returns)}</td>
                  <td data-label="Last updated">{formatDate(p.last_updated)}</td>
                  <td data-label="On the site">
                    <button
                      type="button"
                      className={`admin-switch${p.is_public ? ' on' : ''}`}
                      role="switch"
                      aria-checked={p.is_public}
                      disabled={busy}
                      onClick={() => run(p.id, () => adminApi.updatePlaybook(p.id, { is_public: !p.is_public }),
                        `${p.name} is now ${p.is_public ? 'hidden' : 'shown'} on the site.`)}
                    >
                      <span className="admin-switch-knob" aria-hidden="true" />
                      {p.is_public ? 'Shown' : 'Hidden'}
                    </button>
                  </td>
                  <td className="admin-actions">
                    <button type="button" className="admin-link-button" disabled={busy} onClick={() => setRenaming(p.id)}>
                      Rename
                    </button>
                  </td>
                </tr>
              );
            })}
            {odikai.length === 0 && (
              <tr><td colSpan={7} className="admin-empty">No Odikai playbooks found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function VerifyEmailGate({ user }) {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  return (
    <div className="empty-state">
      <p>
        Admin access needs <strong>{user.email}</strong> to be verified. Send the verification email, click its link,
        then sign out and back in.
      </p>
      <button
        type="button"
        className="btn btn-primary"
        disabled={sent}
        onClick={async () => {
          try {
            await sendEmailVerification(user);
            setSent(true);
          } catch (e) {
            setError(e.message);
          }
        }}
      >
        {sent ? 'Verification email sent' : 'Send verification email'}
      </button>
      {error && <p className="admin-error">{error}</p>}
    </div>
  );
}

export default function AdminPortal() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const allowed = isSuperAdminEmail(user) && user.emailVerified;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [u, p] = await Promise.all([adminApi.listUsers(), adminApi.listPlaybooks()]);
      setUsers(u);
      setPlaybooks(p);
    } catch (e) {
      setLoadError(e.status === 403 ? 'This account is not allowed to use the admin portal.' : e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const notify = (message, isError = false) => setToast({ message, isError });

  // Runs one admin action, shows the outcome, and reloads the lists when the data changed
  const run = async (id, action, successMessage, reload = true) => {
    setBusyId(id);
    try {
      await action();
      notify(successMessage);
      if (reload) {
        const [u, p] = await Promise.all([adminApi.listUsers(), adminApi.listPlaybooks()]);
        setUsers(u);
        setPlaybooks(p);
      }
      return true;
    } catch (e) {
      notify(e.message || 'Something went wrong.', true);
      return false;
    } finally {
      setBusyId(null);
    }
  };

  // Everyone else gets the normal 404, so the page is not advertised
  if (!isSuperAdminEmail(user)) return <NotFound />;

  return (
    <div className="page-wrapper admin-page">
      <div className="page-container">
        <header className="page-header">
          <div className="header-content">
            <h1>Admin</h1>
            <p className="header-subtitle">Manage accounts, custom playbooks and what shows on the site.</p>
          </div>
          {allowed && (
            <div className="header-stats">
              <div className="stat-card">
                <span className="stat-value">{loading ? '-' : users.length}</span>
                <span className="stat-label">Accounts</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{loading ? '-' : playbooks.filter((p) => !p.is_prebuilt).length}</span>
                <span className="stat-label">Custom playbooks</span>
              </div>
            </div>
          )}
        </header>

        {!allowed ? (
          <VerifyEmailGate user={user} />
        ) : (
          <>
            <div className="admin-tabbar">
              <div className="picks-tabs admin-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`picks-tab ${tab === t.id ? 'active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <button type="button" className="btn btn-sm" onClick={load} disabled={loading}>Refresh</button>
            </div>

            {loading ? (
              <div className="loading"><div className="loading-spinner"></div><span>Loading…</span></div>
            ) : loadError ? (
              <>
                <div className="empty-state">
                  <p className="admin-error">{loadError}</p>
                  <button type="button" className="btn" onClick={load}>Try again</button>
                </div>
                <BackendAccess />
              </>
            ) : (
              <>
                {tab === 'overview' && <Overview users={users} playbooks={playbooks} onGo={setTab} />}
                {tab === 'accounts' && <Accounts users={users} run={run} busyId={busyId} notify={notify} />}
                {tab === 'custom' && <CustomPlaybooks playbooks={playbooks} run={run} busyId={busyId} />}
                {tab === 'odikai' && <OdikaiPlaybooks playbooks={playbooks} run={run} busyId={busyId} />}
                {tab === 'content' && <SiteContentEditor notify={notify} />}
              </>
            )}
          </>
        )}
      </div>
      {toast && (
        <div className={`admin-toast${toast.isError ? ' error' : ''}`} role="status">{toast.message}</div>
      )}
    </div>
  );
}
